<template>
  <flow-layout :current-step-index="isBrokerFlow ? 7 : 6">
    <heading level="2" class="heading">{{ $t('reviewSubmit.title') }}</heading>

    <div class="cards">
      <funding-needs-info class="card" :application="application" />
      <applicant-personal-info class="card" :application="application" @modalClose="onModalClose" />
      <business-info class="card" :application="application" @modalClose="onModalClose" />
      <more-business-info class="card" :application="application" @modalClose="onModalClose" />

      <hr class="line" />

      <owners-info
        class="card"
        v-model="owners"
        @jumpToSection="jumpToSection"
        :totalPercentOwnership="totalPercentOwnership"
      />

      <template v-if="hasAdditionalOwners">
        <additional-owners-info
          class="card"
          :application="application"
          :id-of-open-owner="idOfOpenOwner"
          :open-section="openAccordion"
          @toggle="updateOpenOwner"
        />
      </template>
    </div>

    <navigation-buttons
      class="navigationButtons"
      :next="{ label: $t('submitApplicationLabel') }"
      @next="onSubmit"
      @back="goBack"
    ></navigation-buttons>
  </flow-layout>
</template>
<script>
// eslint-disable-next-line import/extensions
import { compare } from 'fast-json-patch/index.mjs';
import { mapGetters, mapActions } from 'vuex';
import FlowLayout from '@/views/layouts/FlowLayout';
import FundingNeedsInfo from '@/views/application/ReviewSubmit/FundingNeedsInfo';
import ApplicantPersonalInfo from '@/views/application/ReviewSubmit/ApplicantPersonalInfo';
import BusinessInfo from '@/views/application/ReviewSubmit/BusinessInfo';
import MoreBusinessInfo from '@/views/application/ReviewSubmit/MoreBusinessInfo';
import OwnersInfo from '@/views/application/ReviewSubmit/OwnersInfo';
import AdditionalOwnersInfo from '@/views/application/ReviewSubmit/AdditionalOwnersInfo';
import { submitDocuments } from '@/modules/api-service/v3';
import applicationMixin from '@/mixins/application';
import brokerFlowMixin from '@/mixins/broker-flow';
import { clearBrokerFlow } from '@/router/guards/broker-flow';

const MAX_PERCENT_OWNERSHIP = 100;

export default {
  name: 'review-submit',
  components: {
    FlowLayout,
    FundingNeedsInfo,
    ApplicantPersonalInfo,
    BusinessInfo,
    MoreBusinessInfo,
    OwnersInfo,
    AdditionalOwnersInfo,
  },
  data() {
    return {
      idOfOpenOwner: '',
      openAccordion: false,
    };
  },
  async created() {
    this.restoreData();
  },
  computed: {
    ...mapGetters('application', ['getApplication']),
    hasAdditionalOwners() {
      return this.application.coOwners.length > 0;
    },
    owners: {
      get() {
        return [{ ...this.application.owners[0] }, ...this.application.coOwners];
      },
      set(newValue) {
        const newOwners = [...newValue];
        const coOwners = newOwners.splice(1);
        this.application = { ...this.application, owners: [newValue[0]], coOwners };
      },
    },
    totalPercentOwnership() {
      return this.owners
        .map(owner => Number(owner.percentOwnership || 0))
        .reduce((a, b) => a + b, 0);
    },
  },
  methods: {
    async onSubmit() {
      if (this.totalPercentOwnership > MAX_PERCENT_OWNERSHIP) {
        return;
      }

      this.$overlay.show();
      try {
        await submitDocuments(this.application.id);
        await this.submitApplication();
        clearBrokerFlow();
      } finally {
        this.$overlay.hide();
      }
    },
    generatePatch() {
      return compare(this.getApplication(), this.application);
    },
    async restoreData() {
      this.application = await this.getApplication();
    },
    onModalClose() {
      this.restoreData();
    },
    jumpToSection(id) {
      this.idOfOpenOwner = id;
      this.openAccordion = true;
    },
    updateOpenOwner({ id, opened }) {
      this.idOfOpenOwner = id;
      this.openAccordion = opened;
    },
  },
  mixins: [applicationMixin, brokerFlowMixin],
};
</script>
<style lang="sass" scoped>
.line
  margin: $glass-spacing-3 0

.heading
  margin-top: $glass-spacing-4

.cards
  margin: $glass-spacing-3 0

  .card + .card
    margin-top: $glass-spacing-2

.agreements
  .subtitle
    margin-top: $glass-spacing-05

.consents
  margin-top: $glass-spacing-3

.navigationButtons
  margin-top: $glass-spacing-6

  +mq($ankylo)
    margin-bottom: $glass-spacing-6
</style>
